import { BatcherService as batch } from './batcher';
import { AUTH_ERROR_MESSAGE } from '../../../../src/app/core/auth/domain/services/auth-service.constants';

const UrlPattern = require('url-pattern');
const merge = require('lodash/fp/merge');
const set = require('lodash/fp/set');
const omit = require('lodash/fp/omit');
const identity = require('lodash/fp/identity');
const startsWith = require('lodash/startsWith');
const cloneDeep = require('lodash/fp/cloneDeep');
const cache = require('memory-cache');
const { TraceId } = require('@buildium/open-tracing');
const Cookies = require('js-cookie');

const cacheNamespace = 'BD_FETCH_';

//Sent cookies and expect json by default
const defaultOptions = {
    credentials: 'same-origin',
    headers: {
        'Accept': 'application/json, text/plain, */*',
        'Content-Type': 'application/json'
    }
};

const defaultActionOptions = {
    cache: false,
    method: 'GET',
    transformResponse: identity,
    transformRequestData: identity,
    isArray: false
};

//Pre and post response handling hooks
let preInterceptor = identity;
let postInterceptor = identity;
let isBatchEnabled = false;

const isFlagOverrideEnabled = window.sessionStorage.getItem('isFlagOverrideEnabled') === 'true';

// Fetch Interceptor
let authService;
let basePathApi = '';

/*
 * Helper functions
 *
 */

function isHttpGet(method) {
    return method.toUpperCase() === 'GET';
}

function isHttpHead(method) {
    return method.toUpperCase() === 'HEAD';
}

function toQueryString(obj, hasQs) {
    const qs = Object.keys(obj).map(function(key) {
        //when we see an array, we want to send a key=value pair for each item
        const vals = Array.isArray(obj[key]) ? obj[key] : [obj[key]];

        return vals.map((val) => {
            return `${encodeURIComponent(key)}=${encodeURIComponent(val)}`;
        }).join('&');
    }).join('&');

    //url might already have a query string
    const prefix = hasQs ? '&' : '?';

    if (qs !== '') {
        return prefix + qs;
    }

    return qs;
}

function handleError(response) {
    //http errors don't reject the promise by default
    if (response.ok) {
        return response;
    } else {
        return new Promise(function executor(resolve, reject) {
            //our error responses have json bodies
            response.json().then(data => {
                response.data = data;
                reject(response);
            }).catch(() => {
                response.data = null;
                reject(response);
            });
        });
    }
}

function parseResponse(response) {
    const contentType = response.headers.get('Content-Type');
    const addHeaders = function addHeadersFn(resp) {
        return {
            response: resp,
            headers: response.headers
        };
    };

    //Handle different response types. Might need to be extended
    if (startsWith(contentType, 'application/json')) {
        return response.json()
            .then(addHeaders)
            .catch(exception => {
                // handle OK responses with no content
                if (+response.headers.get('Content-Length') === 0) {
                    return addHeaders(null);
                }
                throw exception;
            });
    } else if (startsWith(contentType, 'text/')) {
        return response.text().then(addHeaders);
    } else {
        return response.blob().then(addHeaders);
    }
}

/*
 * This is a pretty dumb memory cache, which is what angular-resource uses
 * We can expand this later if we want
 */
function saveCache(url, options) {
    return function saveResponseToCache(response) {
        if (isHttpGet(options.method) && options.cache) {
            cache.put(cacheNamespace + url, cloneDeep(response));
        }
        return response;
    };
}

function handleNull(isArray) {
    return function replaceNull(resp) {
        if (resp.response === null) {
            resp.response = isArray ? [] : {};
        }
        return resp;
    };
}

function createTransformer(transformFn) {
    return function transformer(resp) {
        return transformFn(resp?.response, resp?.headers);
    };
}

function sanitizeUrl(url) {
    const queryIndex = url.indexOf('?');

    if (queryIndex < 0) {
        return url;
    }

    const path = url.substring(0, queryIndex);

    const queryString = url.substring(queryIndex + 1)
        .split('&')
        .filter(param => param.indexOf('=') !== param.length - 1)
        .filter(param => !param.endsWith('=null') && !param.endsWith('=undefined') && !param.endsWith('=NaN'))
        .join('&');

    return `${path}?${queryString}`;
}

function isUrlAuthProtected(url) {
    if (url.startsWith(basePathApi) && !url.startsWith(`${basePathApi}/public`)) {
        return true;
    }
    return false;
}

/*
 * Public functions
 *
 */

/*
 * Wraps the default fetch() with our response handling
 */
function bdFetch(url, options) {
    let fetchOptions = merge(defaultOptions, options || {});
    const xsrfToken = Cookies.get('XSRF-TOKEN');

    fetchOptions = merge(fetchOptions, {
        headers: {
            'X-Correlation-Id': TraceId.generate()
        }
    });

    if (xsrfToken && xsrfToken !== 'null' && xsrfToken !== 'undefined') {
        fetchOptions = merge(fetchOptions, {
            headers: {
                'X-XSRF-TOKEN': xsrfToken,
            }
        });
    }

    const overrideOnFlags = window.sessionStorage.getItem('x-feature-flag-on');
    const overrideOffFlags = window.sessionStorage.getItem('x-feature-flag-off');
    if (overrideOnFlags && isFlagOverrideEnabled) {
        fetchOptions = merge(fetchOptions, {
            headers: {
                'x-feature-flag-on': overrideOnFlags,
            }
        });
    }
    if (overrideOffFlags && isFlagOverrideEnabled) {
        fetchOptions = merge(fetchOptions, {
            headers: {
                'x-feature-flag-off': overrideOffFlags,
            }
        });
    }

    const fetchFn = isBatchEnabled ? batch.fetch : fetch;

    if (authService && authService.isAuthServerApiEnabled && isUrlAuthProtected(url)) {
        return authService.getAccessTokenPromise()
            .then((bearerToken) => {
                fetchOptions = merge(fetchOptions, {
                    headers: {
                        'Authorization': bearerToken,
                    }
                });

                return fetchFn(sanitizeUrl(url), fetchOptions)
                    .then(preInterceptor)
                    .then(handleError)
                    .then(parseResponse)
                    .then(handleNull(fetchOptions.isArray));
            })
            .catch(error => {
                if (error.status === 401 || error.message === AUTH_ERROR_MESSAGE.UserNotAuthenticated) {
                    authService.login();
                }
            });
    } else {
        return fetchFn(sanitizeUrl(url), fetchOptions)
            .then(preInterceptor)
            .then(handleError)
            .then(parseResponse)
            .then(handleNull(fetchOptions.isArray));
    }
}

/*
 * Takes in an options object and some default parameters and returns a function
 * that calls the right service method and returns the resulting data
 */
function createFetchAction(options) {
    const urlPattern = new UrlPattern(options.url);
    const actionOptions = merge(defaultActionOptions, options);
    const defaultParams = actionOptions.params;

    return function fetchFunction(params, data) {
        let userParams;
        let userData;
        let url;
        let fetchPromise;

        //This is what angular-resource does, which is confusing, but we don't have an obviously better way
        if (isHttpGet(actionOptions.method) || isHttpHead(actionOptions.method)) {
            userData = null;
            userParams = merge(defaultParams, params);
        } else if (data) {
            userParams = merge(defaultParams, params);
            userData = data;
        } else {
            userData = params;
            userParams = defaultParams;
        }

        url = urlPattern.stringify(merge(userData, userParams || {}));

        //also something angular-resource does, which is to add unused parameters to the query string
        if (userParams) {
            url += toQueryString(omit(urlPattern.names, userParams), (url.indexOf('?') >= 0));
        }

        if (userData) {
            actionOptions.body = JSON.stringify(actionOptions.transformRequestData(userData));
        }

        const cached = cache.get(cacheNamespace + url);

        if (isHttpGet(actionOptions.method) && actionOptions.cache && cached != null) {
            //we don't want people to inadvertently modify a cached result
            fetchPromise = Promise.resolve(cloneDeep(cached));
        } else {
            fetchPromise = bdFetch(url, omit(['url', 'cache'], actionOptions))
                .then(createTransformer(actionOptions.transformResponse))
                .then(saveCache(url, actionOptions));
        }

        return postInterceptor(fetchPromise);
    };
}

/*
 * Same as above, but takes a map of actions and returns an object with each as a function
 */
function createActions(actions) {
    return Object.keys(actions).reduce(function addNewAction(actionObj, newAction) {
        return set(newAction, createFetchAction(actions[newAction]), actionObj);
    }, {});
}

// eslint-disable-next-line buildium/angular-strict-di
function injectAuthService(serviceReference) {
    authService = serviceReference;
}

/*
 * Should return a response object
 */
function addPreInterceptor(fn) {
    preInterceptor = fn;
}

/*
 * This needs to handle successful and unsuccessful responses
 */
function addPostInterceptor(fn) {
    postInterceptor = fn;
}

function setBatchingEnabled(isEnabled) {
    isBatchEnabled = isEnabled;
}

function setBatcherConfig(config) {
    batch.config = config;
}

function setFetchAuthInterceptorConfig(config) {
    basePathApi = config.basePathApi;
}

export {
    bdFetch as fetch,
    createFetchAction,
    createActions,
    injectAuthService,
    addPreInterceptor,
    addPostInterceptor,
    setBatchingEnabled,
    setBatcherConfig,
    toQueryString,
    setFetchAuthInterceptorConfig
};
